import React from 'react';
import { useApp } from '../context/AppContext';
import { ButtonConfig } from '../types';

const ButtonPad: React.FC = () => {
  const { state, dispatch } = useApp();

  const canEdit = !state.calculator.isLocked || state.calculator.lockedBy === state.currentUser.id;

  const handleButtonClick = (value: string, type: string) => {
    if (!canEdit) return;

    let newExpression = state.calculator.currentExpression;

    switch (type) {
      case 'number':
        newExpression += value;
        break;
      case 'operator':
        if (value === '=') {
          // Calculate result and save to Firestore
          const { result, isError, errorMessage } = calculator.evaluate(newExpression);
          if (isError) {
            dispatch({ type: 'SET_ERROR', payload: { isError: true, message: errorMessage } });
          } else {
            dispatch({ type: 'SET_CURRENT_RESULT', payload: result });
            // Keep the result in the expression so user can continue calculating
            newExpression = result;
            
            // Save to Firestore if in a session
            if (state.currentSession && state.currentUser.id) {
              const calculation = {
                id: Date.now().toString(),
                expression: state.calculator.currentExpression,
                result,
                timestamp: new Date(),
                userId: state.currentUser.id,
                userName: state.currentUser.name,
                userColor: state.currentUser.color,
                isApproved: false,
                approvedBy: [],
                mode: state.calculator.mode,
              };
              
              // Save to Firestore
              import('../services/calculatorService').then(({ calculatorService }) => {
                calculatorService.addCalculation(state.currentSession!.id, calculation);
              }).catch(error => {
                console.error('Failed to save calculation to Firestore:', error);
                // If Firestore fails, add to local state as fallback
                dispatch({ type: 'ADD_CALCULATION', payload: calculation });
              });
            } else {
              // If not in a session, add to local state only
              const calculation = {
                id: Date.now().toString(),
                expression: state.calculator.currentExpression,
                result,
                timestamp: new Date(),
                userId: state.currentUser.id,
                userName: state.currentUser.name,
                userColor: state.currentUser.color,
                isApproved: false,
                approvedBy: [],
                mode: state.calculator.mode,
              };
              dispatch({ type: 'ADD_CALCULATION', payload: calculation });
            }
          }
        } else if (value === 'C') {
          newExpression = '';
          dispatch({ type: 'SET_CURRENT_RESULT', payload: '' });
          dispatch({ type: 'SET_ERROR', payload: { isError: false, message: '' } });
        } else if (value === 'CE') {
          newExpression = newExpression.slice(0, -1);
        } else if (value === '±') {
          if (newExpression.startsWith('-')) {
            newExpression = newExpression.slice(1);
          } else {
            newExpression = '-' + newExpression;
          }
        } else {
          newExpression += value;
        }
        break;
      case 'function':
        if (value === '√') {
          newExpression += 'sqrt(';
        } else if (value === 'x²') {
          newExpression += '^2';
        } else if (value === '%') {
          newExpression += '/100';
        } else {
          newExpression += value + '(';
        }
        break;
      case 'special':
        newExpression += value;
        break;
    }

    dispatch({ type: 'SET_CURRENT_EXPRESSION', payload: newExpression });
    
    // Clear error when user starts typing
    if (state.calculator.isError) {
      dispatch({ type: 'SET_ERROR', payload: { isError: false, message: '' } });
    }
  };

  // Import calculator engine
  const { CalculatorEngine } = require('../utils/calculator');
  const calculator = new CalculatorEngine();
  calculator.setVariables(state.variables);

  // Button configurations for different modes
  const getButtons = (): ButtonConfig[] => {
    const baseButtons: ButtonConfig[] = [
      { label: 'C', value: 'C', type: 'operator', className: 'bg-danger-600 hover:bg-danger-700 text-white' },
      { label: 'CE', value: 'CE', type: 'operator', className: 'bg-warning-600 hover:bg-warning-700 text-white' },
      { label: '±', value: '±', type: 'operator', className: 'bg-dark-600 hover:bg-dark-700 text-white' },
      { label: '÷', value: '/', type: 'operator', className: 'bg-accent-600 hover:bg-accent-700 text-white' },
      
      { label: '7', value: '7', type: 'number' },
      { label: '8', value: '8', type: 'number' },
      { label: '9', value: '9', type: 'number' },
      { label: '×', value: '*', type: 'operator', className: 'bg-accent-600 hover:bg-accent-700 text-white' },
      
      { label: '4', value: '4', type: 'number' },
      { label: '5', value: '5', type: 'number' },
      { label: '6', value: '6', type: 'number' },
      { label: '−', value: '-', type: 'operator', className: 'bg-accent-600 hover:bg-accent-700 text-white' },
      
      { label: '1', value: '1', type: 'number' },
      { label: '2', value: '2', type: 'number' },
      { label: '3', value: '3', type: 'number' },
      { label: '+', value: '+', type: 'operator', className: 'bg-accent-600 hover:bg-accent-700 text-white' },
      
      { label: '0', value: '0', type: 'number', className: 'col-span-2' },
      { label: '.', value: '.', type: 'number' },
      { label: '=', value: '=', type: 'operator', className: 'bg-success-600 hover:bg-success-700 text-white' },
    ];

    const scientificButtons: ButtonConfig[] = [
      { label: 'sin', value: 'sin', type: 'function', mode: ['scientific'] },
      { label: 'cos', value: 'cos', type: 'function', mode: ['scientific'] },
      { label: 'tan', value: 'tan', type: 'function', mode: ['scientific'] },
      { label: 'log', value: 'log', type: 'function', mode: ['scientific'] },
      
      { label: 'ln', value: 'ln', type: 'function', mode: ['scientific'] },
      { label: 'π', value: 'pi', type: 'special', mode: ['scientific'] },
      { label: 'e', value: 'e', type: 'special', mode: ['scientific'] },
      { label: 'x²', value: 'x²', type: 'function', mode: ['scientific'] },
      
      { label: '√', value: '√', type: 'function', mode: ['scientific'] },
      { label: 'x^y', value: '^', type: 'operator', mode: ['scientific'] },
      { label: '(', value: '(', type: 'special', mode: ['scientific'] },
      { label: ')', value: ')', type: 'special', mode: ['scientific'] },
    ];

    const programmerButtons: ButtonConfig[] = [
      { label: 'AND', value: '&', type: 'operator', mode: ['programmer'] },
      { label: 'OR', value: '|', type: 'operator', mode: ['programmer'] },
      { label: 'XOR', value: '^', type: 'operator', mode: ['programmer'] },
      { label: 'NOT', value: '~', type: 'operator', mode: ['programmer'] },
      
      { label: '<<', value: '<<', type: 'operator', mode: ['programmer'] },
      { label: '>>', value: '>>', type: 'operator', mode: ['programmer'] },
      { label: 'BIN', value: 'bin', type: 'special', mode: ['programmer'] },
      { label: 'HEX', value: 'hex', type: 'special', mode: ['programmer'] },
    ];

    let buttons = [...baseButtons];

    if (state.calculator.mode === 'scientific') {
      buttons = [...scientificButtons, ...baseButtons];
    } else if (state.calculator.mode === 'programmer') {
      buttons = [...programmerButtons, ...baseButtons];
    }

    return buttons.filter(button => 
      !button.mode || button.mode.includes(state.calculator.mode)
    );
  };

  const buttons = getButtons();

  return (
    <div className="bg-dark-800 rounded-xl p-1 sm:p-2 shadow-lg border border-dark-700 h-full flex flex-col">
      <div className="grid grid-cols-4 gap-0.5 sm:gap-1 flex-1 max-h-full">
        {buttons.map((button, index) => (
          <button
            key={index}
            onClick={() => handleButtonClick(button.value, button.type)}
            disabled={!canEdit}
            className={`
              ${button.className || 'bg-dark-700 hover:bg-dark-600 text-white'}
              ${button.className?.includes('col-span-2') ? 'col-span-2' : ''}
              font-semibold py-1.5 sm:py-2 px-1 sm:px-2 rounded-lg transition-all duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              active:scale-95 transform transition-transform
              text-xs sm:text-sm shadow-sm hover:shadow-md
              focus:outline-none focus:ring-2 focus:ring-accent-500
              min-h-[28px] sm:min-h-[32px] lg:min-h-[36px]
            `}
          >
            {button.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ButtonPad;
